import type { ReactNode } from 'react'
import { useConfig } from '@/config/ConfigProvider'
import { isMockConfig } from '@/config/runtime'
import { Auth0AuthProvider } from './Auth0AuthProvider'
import { MockAuthProvider } from './MockAuthProvider'

/**
 * Picks the session backing from runtime configuration.
 *
 * The flow it fronts, unchanged from the shape this file has carried since the
 * SPA was scaffolded:
 *
 *   1. "Continue with Google" → Auth0 /authorize, PKCE, connection=google-oauth2
 *   2. callback → token exchange → access token held in memory, never localStorage
 *   3. every API call sends Authorization: Bearer <token>  (seam: ./token.ts)
 *   4. the API validates the JWT, resolves the tenant, sets the RLS session var
 *
 * The SPA never decides authorization. It hides controls the user cannot use;
 * the API rejects them regardless.
 *
 * Step 3 has a seam but no caller: `src/api` is still the mock adapter until
 * APP-51. Logging in is real; the data behind it is not yet.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const config = useConfig()

  if (isMockConfig(config)) return <MockAuthProvider>{children}</MockAuthProvider>

  return <Auth0AuthProvider config={config.auth}>{children}</Auth0AuthProvider>
}
